import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Search, CheckCircle, XCircle, Loader, AlertTriangle, X } from 'lucide-react';
import superAdminService from '../../services/superadmin/superAdminService';
import { Admin } from '../../types/api.types';
import toast from 'react-hot-toast';

const AdminManagement: React.FC = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; admin: Admin | null }>({
    show: false,
    admin: null,
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  // Fetch all admins from API
  const fetchAdmins = async () => {
    // Check if token exists before making API call
    const token = localStorage.getItem('authToken');
    if (!token || !token.trim()) {
      console.warn('⚠️ No token found - cannot fetch admins');
      setLoading(false);
      // Redirect to login if no token
      if (!window.location.pathname.includes('/login')) {
        window.location.replace('/admin/login');
      }
      return;
    }

    try {
      setLoading(true);
      const data = await superAdminService.getAllAdmins();
      setAdmins(data);
    } catch (error: any) {
      // Handle different error types
      if (error?.isAuthError || 
          error?.message?.includes('Authentication token not found') || 
          error?.response?.status === 401 ||
          error?.response?.data?.message?.toLowerCase().includes('no token') ||
          error?.response?.data?.message?.toLowerCase().includes('unauthorized')) {
        // Token missing or invalid - redirect to login
        console.warn('⚠️ Authentication error - redirecting to login');
        if (!window.location.pathname.includes('/login')) {
          // Clear auth data
          localStorage.removeItem('authToken');
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('userRole');
          localStorage.removeItem('userEmail');
          // Redirect immediately
          window.location.href = '/admin/login';
        }
        // Don't show error toast for auth errors
        return;
      } else {
        toast.error(error.response?.data?.message || error.message || 'Failed to fetch admins');
      }
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats from real data
  const stats = {
    total: admins.length,
    active: admins.filter(a => a.is_active === 1).length,
    inactive: admins.filter(a => a.is_active === 0).length,
    thisMonth: admins.filter(a => {
      const createdDate = new Date(a.created_at);
      const now = new Date();
      return createdDate.getMonth() === now.getMonth() && 
             createdDate.getFullYear() === now.getFullYear();
    }).length,
  };

  // Filter admins based on search and status
  const filteredAdmins = admins.filter(admin => {
    const matchesSearch = 
      admin.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === 'all' ? true :
      statusFilter === 'active' ? admin.is_active === 1 :
      admin.is_active === 0;

    return matchesSearch && matchesStatus;
  });

  // Toggle admin status
  const handleToggleStatus = async (admin: Admin) => {
    try {
      const newStatus = admin.is_active === 1 ? false : true;
      await superAdminService.updateAdminStatus({
        admin_id: admin.admin_id,
        is_active: newStatus,
      });
      toast.success(`Admin ${newStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchAdmins(); // Refresh list
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (admin: Admin) => {
    setDeleteModal({ show: true, admin });
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModal({ show: false, admin: null });
  };

  // Confirm delete admin
  const confirmDelete = async () => {
    if (!deleteModal.admin) return;

    try {
      setDeleting(true);
      await superAdminService.deleteAdmin({ admin_id: deleteModal.admin.admin_id });
      toast.success('Admin deleted successfully!');
      closeDeleteModal();
      fetchAdmins(); // Refresh list
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete admin');
    } finally {
      setDeleting(false);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get time ago
  const getTimeAgo = (dateString: string | null) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) return `${minutes} minutes ago`;
    if (hours < 24) return `${hours} hours ago`;
    return `${days} days ago`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">Admin Management</h1>
          <p className="text-secondary-600">Create and manage admin accounts</p>
        </div>
        <button 
          onClick={() => navigate('/super-admin/admin-management/add')}
          className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 transition-all hover:scale-105 shadow-green"
        >
          <Plus size={20} />
          <span>Add New Admin</span>
        </button>
      </div>

      {/* Stats - Real Data */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-soft">
          <p className="text-sm text-secondary-600 mb-2">Total Admins</p>
          <p className="text-3xl font-bold text-primary-600">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-soft">
          <p className="text-sm text-secondary-600 mb-2">Active Admins</p>
          <p className="text-3xl font-bold text-success-600">{stats.active}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-soft">
          <p className="text-sm text-secondary-600 mb-2">Inactive Admins</p>
          <p className="text-3xl font-bold text-warning-600">{stats.inactive}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-soft">
          <p className="text-sm text-secondary-600 mb-2">Added This Month</p>
          <p className="text-3xl font-bold text-info-600">{stats.thisMonth}</p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
            <input
              type="text"
              placeholder="Search admins by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
            />
          </div>
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Admins Table - Real Data */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader className="animate-spin text-primary-500" size={48} />
          </div>
        ) : filteredAdmins.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-secondary-600">No admins found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary-50 border-b">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-secondary-900">Admin</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-secondary-900">Contact</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-secondary-900">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-secondary-900">Last Login</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-secondary-900">Created</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-secondary-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredAdmins.map((admin) => (
                  <tr key={admin.admin_id} className="hover:bg-secondary-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold">
                          {admin.full_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-secondary-900">{admin.full_name}</p>
                          <p className="text-sm text-secondary-600">Admin</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-secondary-900">{admin.email}</p>
                        <p className="text-sm text-secondary-600">{admin.mobile_number || 'N/A'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleStatus(admin)}
                        className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold transition-all hover:scale-105 ${
                          admin.is_active === 1 
                            ? 'bg-success-50 text-success-600 hover:bg-success-100' 
                            : 'bg-danger-50 text-danger-600 hover:bg-danger-100'
                        }`}
                      >
                        {admin.is_active === 1 ? <CheckCircle size={14} /> : <XCircle size={14} />}
                        <span>{admin.is_active === 1 ? 'Active' : 'Inactive'}</span>
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-secondary-600">
                      {getTimeAgo(admin.last_login_at)}
                    </td>
                    <td className="px-6 py-4 text-sm text-secondary-600">
                      {formatDate(admin.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center space-x-2">
                        <button 
                          onClick={() => navigate(`/super-admin/admin-management/edit/${admin.admin_id}`)}
                          className="bg-warning-50 hover:bg-warning-100 text-warning-600 p-2 rounded-lg transition-colors"
                          title="Edit Admin"
                        >
                          <Edit size={18} />
                        </button>
                        <button 
                          onClick={() => openDeleteModal(admin)}
                          className="bg-danger-50 hover:bg-danger-100 text-danger-600 p-2 rounded-lg transition-colors"
                          title="Delete Admin"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && deleteModal.admin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-slide-up">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="bg-danger-100 p-3 rounded-full">
                  <AlertTriangle className="text-danger-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">Delete Admin</h2>
              </div>
              <button
                onClick={closeDeleteModal}
                className="text-secondary-400 hover:text-secondary-600 transition-colors"
                disabled={deleting}
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <p className="text-secondary-700 mb-4">
                Are you sure you want to delete this admin? This action cannot be undone.
              </p>
              
              {/* Admin Details */}
              <div className="bg-secondary-50 rounded-lg p-4 mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-danger-500 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                    {deleteModal.admin.full_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-secondary-900">{deleteModal.admin.full_name}</p>
                    <p className="text-sm text-secondary-600">{deleteModal.admin.email}</p>
                  </div>
                </div>
              </div>

              <div className="bg-danger-50 border border-danger-200 rounded-lg p-3">
                <p className="text-sm text-danger-700 flex items-start space-x-2">
                  <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                  <span>All data associated with this admin will be permanently deleted.</span>
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex space-x-3 p-6 bg-secondary-50 rounded-b-2xl">
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                className="flex-1 px-6 py-3 bg-white border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 px-6 py-3 bg-danger-500 hover:bg-danger-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={20} />
                    <span>Delete Admin</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;
